import { Coding } from "@medplum/fhirtypes";

import { SubsectionAnswerType } from "@/types/questionnaireSections";

import type { InsuranceVerificationTier } from "../types/verify-insurance";

/** Section linkId for the billing block in the intake questionnaire. Shared by Verify Insurance and client-billing. */
export const BILLING_SECTION_LINK_ID = "billingInformation";

/** LinkIds for Step 1 (Subscriber) per tier. Phone/type/country live only on Step 2 (insurance). */
export const TIER_SUBSCRIBER_LINK_IDS: Record<
  InsuranceVerificationTier,
  {
    relationship: string;
    firstName: string;
    lastName: string;
    middleName: string;
    dateOfBirth: string;
    subscriberId: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  }
> = {
  primary: {
    relationship: "billingInformationSubscriberRelationship",
    firstName: "billingInformationSubscriberFirstName",
    lastName: "billingInformationSubscriberLastName",
    middleName: "billingInformationSubscriberMiddleName",
    dateOfBirth: "billingInformationSubscriberDateOfBirth",
    subscriberId: "billingInformationSubscriberID",
    address: "billingInformationSubscriberStreetAddress",
    city: "billingInformationSubscriberCity",
    state: "billingInformationSubscriberState",
    zipCode: "billingInformationSubscriberZipCode",
  },
  secondary: {
    relationship: "billingInformationSecondInsuranceSubscriberRelationship",
    firstName: "billingInformationSecondInsuranceSubscriberFirstName",
    lastName: "billingInformationSecondInsuranceSubscriberLastName",
    middleName: "billingInformationSecondInsuranceSubscriberMiddleName",
    dateOfBirth: "billingInformationSecondInsuranceSubscriberDateOfBirth",
    subscriberId: "billingInformationSecondInsuranceSubscriberID",
    address: "billingInformationSecondInsuranceSubscriberStreetAddress",
    city: "billingInformationSecondInsuranceSubscriberCity",
    state: "billingInformationSecondInsuranceSubscriberState",
    zipCode: "billingInformationSecondInsuranceSubscriberZipCode",
  },
  tertiary: {
    relationship: "billingInformationThirdInsuranceSubscriberRelationship",
    firstName: "billingInformationThirdInsuranceSubscriberFirstName",
    lastName: "billingInformationThirdInsuranceSubscriberLastName",
    middleName: "billingInformationThirdInsuranceSubscriberMiddleName",
    dateOfBirth: "billingInformationThirdInsuranceSubscriberDateOfBirth",
    subscriberId: "billingInformationThirdInsuranceSubscriberID",
    address: "billingInformationThirdInsuranceSubscriberStreetAddress",
    city: "billingInformationThirdInsuranceSubscriberCity",
    state: "billingInformationThirdInsuranceSubscriberState",
    zipCode: "billingInformationThirdInsuranceSubscriberZipCode",
  },
};

/** LinkIds for Step 2 (Insurance) per tier. Matches questionnaire qh-intake-forms.json. */
export const TIER_INSURANCE_LINK_IDS: Record<
  InsuranceVerificationTier,
  {
    company: string;
    id: string;
    groupNumber: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
    type: string;
    country: string;
    addressDetails: string;
  }
> = {
  primary: {
    company: "billingInformationInsuranceCompany",
    id: "billingInformationInsuranceId",
    groupNumber: "billingInformationInsuranceGroupNumber",
    address: "billingInformationAddress",
    city: "billingInformationCity",
    state: "billingInformationState",
    zipCode: "billingInformationZipCode",
    phone: "billingInformationPhone",
    type: "billingInformationType",
    country: "billingInformationCountry",
    addressDetails: "billingInformationAddressDetails",
  },
  secondary: {
    company: "billingInformationSecondInsuranceCompany",
    id: "billingInformationSecondInsuranceId",
    groupNumber: "billingInformationSecondInsuranceGroupNumber",
    address: "billingInformationSecondInsuranceAddress",
    city: "billingInformationSecondInsuranceCity",
    state: "billingInformationSecondInsuranceState",
    zipCode: "billingInformationSecondInsuranceZipCode",
    phone: "billingInformationSecondInsurancePhone",
    type: "billingInformationSecondInsuranceType",
    country: "billingInformationSecondInsuranceCountry",
    addressDetails: "billingInformationSecondInsuranceAddressDetails",
  },
  tertiary: {
    company: "billingInformationThirdInsuranceCompany",
    id: "billingInformationThirdInsuranceId",
    groupNumber: "billingInformationThirdInsuranceGroupNumber",
    address: "billingInformationThirdInsuranceAddress",
    city: "billingInformationThirdInsuranceCity",
    state: "billingInformationThirdInsuranceState",
    zipCode: "billingInformationThirdInsuranceZipCode",
    phone: "billingInformationThirdInsurancePhone",
    type: "billingInformationThirdInsuranceType",
    country: "billingInformationThirdInsuranceCountry",
    addressDetails: "billingInformationThirdInsuranceAddressDetails",
  },
};

function getAnswer(
  answers: SubsectionAnswerType[] | undefined,
  linkId: string,
): SubsectionAnswerType | undefined {
  return answers?.find((a) => a.linkId === linkId);
}

/** Returns a string for form initial value; empty string when missing. */
export function getVerifyAnswerString(
  answers: SubsectionAnswerType[] | undefined,
  linkId: string,
): string {
  const answer = getAnswer(answers, linkId);
  if (answer?.value == null || answer.value === "") return "";
  const v = answer.value;
  if (typeof v === "object" && v !== null && "display" in v)
    return (v as Coding).display ?? (v as Coding).code ?? "";
  return v?.toString() ?? "";
}

/** Reads the stored coding for a linkId through `toStoredCoding`; undefined when none is stored. */
export function getVerifyAnswerCoding(
  answers: SubsectionAnswerType[] | undefined,
  linkId: string,
): Coding | undefined {
  const answer = getAnswer(answers, linkId);
  if (!answer?.value || typeof answer.value !== "object" || !("code" in answer.value))
    return undefined;
  return toStoredCoding(answer.value as Coding);
}

/**
 * Returns a stored coding as it is, with a missing `code` defaulted to "". Every stored key is
 * kept and none is added, so a picked value reads back deep-equal to what the save stored.
 * @param coding Coding read from a QuestionnaireResponse answer.
 * @returns A copy of the coding.
 */
export function toStoredCoding(coding: Coding): Coding {
  return { ...coding, code: coding.code ?? "" };
}
